import { useEffect, useState } from 'react'
import {
  Button,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Autocomplete,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Table,
  Paper,
  TableContainer,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminTeamsService } from '../../../services/admin/teams'
import { toast } from 'react-toastify'
import { processTemplateService } from '../../../services/processTemplateService'
import { adminUserReplacementService } from '../../../services/admin/userReplacement'
import {
  CancelOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterAltOffOutlined,
  FilterAltOutlined,
} from '@mui/icons-material'
import { FORMATS } from '../../../utility/constants'
import dayjs from 'dayjs'

const UserReplacement = () => {
  const { user } = useAuthContext()

  const [openDialog, setOpenDialog] = useState(false)
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [users, setUsers] = useState([])
  const [processes, setProcesses] = useState([])
  const [replacements, setReplacements] = useState([])
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteRow, setDeleteRow] = useState(null)
  const [logDialogOpen, setLogDialogOpen] = useState(false)
  const [logs, setLogs] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [showLogFilters, setShowLogFilters] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewTasks, setPreviewTasks] = useState([])
  const [selectedTasks, setSelectedTasks] = useState([])
  const [selectedTaskObjects, setSelectedTaskObjects] = useState([])
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    process: '',
    startDate: '',
    endDate: '',
  })
  const [logFilters, setLogFilters] = useState({
    search: '',
    fromDate: '',
    toDate: '',
  })

  const handleDialogClose = () => {
    setOpenDialog(false)
    setFormData({})
    setIsEdit(false)
    setEditingId(null)
  }

  const fetchUsers = async () => {
    const res = await adminTeamsService.getUsers(user)
    setUsers(res || [])
  }

  const fetchProcesses = async () => {
    const res = await processTemplateService.getAllProcessTemplates(user)
    setProcesses(res || [])
  }

  const fetchUserReplacements = async () => {
    try {
      const res = await adminUserReplacementService.getUserReplacement(user)
      setReplacements(res || [])
    } catch (err) {
      console.error(err)
      toast.error('Error fetching replacements')
    }
  }

  useEffect(() => {
    if (user) {
      fetchUsers()
      fetchProcesses()
      fetchUserReplacements()
    }
  }, [user])

  const handleChange = (value, field) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleDeleteClick = (row) => {
    setDeleteRow(row)
    setDeleteDialogOpen(true)
  }

  const getFullName = (u) => (u ? `${u.first_name} ${u.last_name}` : 'Unknown')

  const currentUserName = getFullName(user)

  const handleViewLog = async () => {
    try {
      const res = await adminUserReplacementService.getUserReplacementLogs(user)
      const data = res?.rows || res || []
      setLogs(data.filter((item) => item !== null && item !== undefined))
      setLogDialogOpen(true)
    } catch (err) {
      console.error(err)
      toast.error('Error fetching logs')
    }
  }

  const filteredReplacements = replacements.filter((row) => {
    const fromUsers = users.find((u) => u.id === row.from_user_id)
    const toUsers = users.find((u) => u.id === row.to_user_id)
    const processTemplates = processes.find((p) => p.id === row.process_template_id)
    const fromName = fromUsers ? `${fromUsers.first_name} ${fromUsers.last_name}`.toLowerCase() : ''
    const toName = toUsers ? `${toUsers.first_name} ${toUsers.last_name}`.toLowerCase() : ''
    const processName = processTemplates ? processTemplates.process_template_name.toLowerCase() : ''
    const rowDate = row.created_at ? dayjs(row.created_at) : null
    const isAfterStart =
      !filters.startDate || (rowDate && rowDate.isAfter(dayjs(filters.startDate).startOf('day')))
    const isBeforeEnd =
      !filters.endDate || (rowDate && rowDate.isBefore(dayjs(filters.endDate).endOf('day')))
    return (
      fromName.includes(filters.from) &&
      toName.includes(filters.to) &&
      processName.includes(filters.process) &&
      isAfterStart &&
      isBeforeEnd
    )
  })

  const handleSave = async () => {
    try {
      const newErrors = {}
      if (!formData?.from_user_id) newErrors.from_user_id = 'Old user is required'
      if (!formData?.to_user_id) newErrors.to_user_id = 'New user is required'
      if (
        formData?.from_user_id?.id &&
        formData?.to_user_id?.id &&
        formData.from_user_id.id === formData.to_user_id.id
      ) {
        newErrors.to_user_id = 'Old and New user cannot be same'
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        toast.error('Please fill required fields')
        return
      }
      setConfirmDialogOpen(true)
    } catch (err) {
      console.error(err)
      toast.error('Error preparing save')
    }
  }

  const formatDate = (date, format) => {
    if (!date) return '-'
    return dayjs(date).format(format)
  }

  const saveReplacement = async (tasksAssigned = false) => {
    const payload = {
      id: editingId,
      from_user_id: formData?.from_user_id?.id || null,
      to_user_id: formData?.to_user_id?.id || null,
      process_template_id: formData?.process_templates?.id || null,
    }
    const fromName = getFullName(formData?.from_user_id)
    const toName = getFullName(formData?.to_user_id)
    const processName = formData?.process_templates?.process_template_name || 'All Processes'
    const taskNames = getTaskNamesString()
    if (isEdit) {
      const oldRecord = replacements.find((r) => r.id === editingId)
      const oldFromUser = users.find((u) => u.id === oldRecord?.from_user_id)
      const oldToUser = users.find((u) => u.id === oldRecord?.to_user_id)
      const oldFromName = getFullName(oldFromUser)
      const oldToName = getFullName(oldToUser)
      await adminUserReplacementService.updateUserReplacement(user, payload)
      let logMessage = `${currentUserName} updated replacement: from ${oldFromName} → ${oldToName} to ${fromName} → ${toName} for Process: ${processName}`

      if (tasksAssigned && taskNames) {
        logMessage += ` and Tasks updated are : ${taskNames}`
      }

      await adminUserReplacementService.addUserReplacementLog(user, { log: logMessage })
    } else {
      await adminUserReplacementService.addUserReplacement(user, payload)

      let logMessage = `${currentUserName} added replacement: ${fromName} → ${toName} for Process: ${processName}`

      if (tasksAssigned && taskNames) {
        logMessage += ` and Tasks moved are : ${taskNames}`
      }

      await adminUserReplacementService.addUserReplacementLog(user, { log: logMessage })
    }

    await fetchUserReplacements()
    toast.success(isEdit ? 'Updated successfully!' : 'Saved successfully!')
    setConfirmDialogOpen(false)
    handleDialogClose()
    setIsEdit(false)
    setEditingId(null)
  }

  const handleFinalSave = async (assignActiveTasks) => {
    try {
      const payload = {
        from_user_id: formData?.from_user_id?.id,
        process_template_id: formData?.process_templates?.id || null,
      }
      if (assignActiveTasks) {
        const res = await adminUserReplacementService.previewTasks(user, payload)
        const tasks = res.tasks || []

        if (tasks.length === 0) {
          await saveReplacement(false)
          return
        }
        setPreviewTasks(tasks)
        setSelectedTasks(tasks.map((t) => t.id))
        setSelectedTaskObjects(tasks)
        setPreviewModalOpen(true)
        return
      }
      await saveReplacement()
    } catch (err) {
      console.error(err)
      toast.error('Error saving')
    }
  }

  const handleEdit = (row) => {
    const fromUser = users.find((u) => u.id === row.from_user_id)
    const toUser = users.find((u) => u.id === row.to_user_id)
    const processTemplate = processes.find((p) => p.id === row.process_template_id)
    setFormData({
      from_user_id: fromUser || null,
      to_user_id: toUser || null,
      process_templates: processTemplate || null,
    })
    setEditingId(row.id)
    setIsEdit(true)
    setOpenDialog(true)
  }

  const handleDelete = async (row) => {
    try {
      const fromUser = users.find((u) => u.id === row.from_user_id)
      const toUser = users.find((u) => u.id === row.to_user_id)
      const fromName = getFullName(fromUser)
      const toName = getFullName(toUser)
      await adminUserReplacementService.deleteUserReplacement(user, row.id)
      const processName =
        processes.find((p) => p.id === row.process_template_id)?.process_template_name ||
        'All Processes'
      const logMessage = `${currentUserName} deleted replacement: ${fromName} → ${toName} for Process: ${processName}`
      await adminUserReplacementService.addUserReplacementLog(user, { log: logMessage })
      await fetchUserReplacements()
      toast.success('Deleted successfully!')
    } catch (err) {
      console.error(err)
      toast.error('Error deleting')
    }
  }

  const filteredLogs = logs
    .filter((log) => log !== null && log !== undefined)
    .filter((log) => {
      const searchMatch =
        !logFilters.search || (log.log || '').toLowerCase().includes(logFilters.search)
      const logDate = log.created_at ? dayjs(log.created_at) : null
      const isAfterStart =
        !logFilters.fromDate ||
        (logDate && logDate.isAfter(dayjs(logFilters.fromDate).startOf('day')))
      const isBeforeEnd =
        !logFilters.toDate || (logDate && logDate.isBefore(dayjs(logFilters.toDate).endOf('day')))
      return searchMatch && isAfterStart && isBeforeEnd
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const filterInputStyle = {
    '& .MuiOutlinedInput-root': {
      height: '20px',
      fontSize: '11px',
      backgroundColor: '#fff',
    },
  }

  const dateInputStyle = {
    '& .MuiOutlinedInput-root': {
      height: '20px',
      fontSize: '11px',
      backgroundColor: '#fff',
      marginBottom: '4px',
    },
    '& input': {
      padding: '2px',
    },
  }

  const initialFilters = {
    from: '',
    to: '',
    process: '',
    startDate: '',
    endDate: '',
  }

  const handleToggleFilters = () => {
    setShowFilters((prev) => {
      const next = !prev
      if (!next) {
        setFilters(initialFilters)
      }
      return next
    })
  }

  const handleLogFilterToggle = () => {
    setShowLogFilters((prev) => {
      const next = !prev
      if (!next) {
        setLogFilters({
          search: '',
          fromDate: '',
          toDate: '',
        })
      }
      return next
    })
  }

  const toggleTask = (task) => {
    setSelectedTaskObjects((prev) => {
      const exists = prev.find((t) => t.id === task.id)
      if (exists) {
        return prev.filter((t) => t.id !== task.id)
      } else {
        return [...prev, task]
      }
    })
    setSelectedTasks((prev) =>
      prev.includes(task.id) ? prev.filter((id) => id !== task.id) : [...prev, task.id],
    )
  }

  const getTaskNamesString = () => {
    if (!selectedTaskObjects || selectedTaskObjects.length === 0) return ''
    return selectedTaskObjects
      .map((t) => t?.reference_no || t?.task_name || '')
      .filter(Boolean) // remove empty values
      .join(', ')
  }

  const handleSubmitSelected = async () => {
    await adminUserReplacementService.applyTasks(user, {
      to_user_id: formData?.to_user_id?.id,
      selected_task_ids: selectedTasks,
    })
    await saveReplacement(true)
    setPreviewModalOpen(false)
    setSelectedTasks([])
    setSelectedTaskObjects([])
  }

  return (
    <>
      <Container>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 20,
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ color: '#656d92' }}>
            User Replacement
          </Typography>

          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title={showFilters ? 'Clear Filters' : 'Show Filters'}>
              <IconButton size="small" onClick={handleToggleFilters}>
                {showFilters ? <FilterAltOffOutlined /> : <FilterAltOutlined />}
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              size="small"
              onClick={() => {
                handleViewLog()
                setShowLogFilters(false)
              }}
            >
              View Log
            </Button>

            <Button variant="contained" size="small" onClick={() => setOpenDialog(true)}>
              Add Replacement
            </Button>
          </Box>
        </div>
      </Container>

      {/* MAIN TABLE */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: 3,
          overflowX: 'auto',
          ml: '24px',
          width: '96%',
        }}
      >
        <Table sx={{ minWidth: 800, tableLayout: 'fixed' }}>
          <TableHead>
            {/* HEADER */}
            <TableRow sx={{ backgroundColor: '#f7f9ff' }}>
              <TableCell sx={{ width: '20px' }}>
                <b>Sl No</b>
              </TableCell>
              <TableCell sx={{ width: '80px' }}>
                <b>From Users</b>
              </TableCell>
              <TableCell sx={{ width: '80px' }}>
                <b>To Users</b>
              </TableCell>
              <TableCell sx={{ width: '80px' }}>
                <b>Process Templates</b>
              </TableCell>
              <TableCell sx={{ width: '90px' }}>
                <b>Created At</b>
              </TableCell>
              <TableCell align="center" sx={{ width: '120px' }}>
                <b>Actions</b>
              </TableCell>
            </TableRow>

            {/* FILTER ROW */}
            {showFilters && (
              <TableRow
                sx={{
                  backgroundColor: '#fafbff',
                  '& .MuiTableCell-root': {
                    py: 1,
                    px: 1,
                    borderBottom: '1px solid #e0e0e0',
                  },
                }}
              >
                <TableCell />

                {/* FROM USERS */}
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.from}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        from: e.target.value.toLowerCase(),
                      }))
                    }
                    sx={filterInputStyle}
                  />
                </TableCell>

                {/* TO USERS */}
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.to}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        to: e.target.value.toLowerCase(),
                      }))
                    }
                    sx={filterInputStyle}
                  />
                </TableCell>

                {/* PROCESS */}
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.process}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        process: e.target.value.toLowerCase(),
                      }))
                    }
                    sx={filterInputStyle}
                  />
                </TableCell>

                {/* DATE RANGE */}
                <TableCell>
                  <Box display="flex" gap={1}>
                    <TextField
                      type="date"
                      size="small"
                      value={filters.startDate}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      sx={dateInputStyle}
                    />
                    <TextField
                      type="date"
                      size="small"
                      value={filters.endDate}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      sx={dateInputStyle}
                    />
                  </Box>
                </TableCell>

                <TableCell />
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {(showFilters ? filteredReplacements : replacements).length > 0 ? (
              (showFilters ? filteredReplacements : replacements).map((row, index) => {
                const fromUsers = users.find((u) => u.id === row.from_user_id)
                const toUsers = users.find((u) => u.id === row.to_user_id)
                const processTemplates = processes.find((p) => p.id === row.process_template_id)

                return (
                  <TableRow key={row.id}>
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      {fromUsers
                        ? `${fromUsers.first_name} ${fromUsers.last_name}`
                        : row.from_user_id}
                    </TableCell>

                    <TableCell>
                      {toUsers ? `${toUsers.first_name} ${toUsers.last_name}` : row.to_user_id}
                    </TableCell>

                    <TableCell>
                      {processTemplates ? processTemplates.process_template_name : '-'}
                    </TableCell>

                    <TableCell>{formatDate(row.created_at, FORMATS.DATETIME)}</TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(row)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteClick(row)}>
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No Data Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ADD / EDIT DIALOG */}
      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? 'Edit Replacement' : 'Add Replacement'}</DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* OLD USER */}
            <Autocomplete
              options={users}
              value={formData.from_user_id || null}
              getOptionLabel={(u) =>
                `${u.first_name} ${u.last_name} ${u.email ? `(${u.email})` : ''}`
              }
              onChange={(e, val) => {
                handleChange(val, 'from_user_id')
                setErrors((prev) => ({ ...prev, from_user_id: '' }))
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Old User"
                  size="small"
                  error={!!errors.from_user_id}
                  helperText={errors.from_user_id}
                  fullWidth
                />
              )}
            />

            {/* NEW USER */}
            <Autocomplete
              options={users}
              value={formData.to_user_id || null}
              getOptionLabel={(u) =>
                `${u.first_name} ${u.last_name} ${u.email ? `(${u.email})` : ''}`
              }
              onChange={(e, val) => {
                handleChange(val, 'to_user_id')
                setErrors((prev) => ({ ...prev, to_user_id: '' }))
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New User"
                  size="small"
                  error={!!errors.to_user_id}
                  helperText={errors.to_user_id}
                  fullWidth
                />
              )}
            />

            {/* PROCESS TEMPLATE */}
            <Autocomplete
              options={processes}
              value={formData.process_templates || null}
              getOptionLabel={(p) => p.process_template_name}
              onChange={(e, val) => handleChange(val, 'process_templates')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Process Template" size="small" fullWidth />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !formData?.from_user_id ||
              !formData?.to_user_id ||
              formData?.from_user_id?.id === formData?.to_user_id?.id
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRM ACTIVE TASKS DIALOG */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Do you want to assign the current active tasks to the new user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false)
              handleFinalSave(false)
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmDialogOpen(false)
              handleFinalSave(true)
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRM DIALOG */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this record?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleDelete(deleteRow)
              setDeleteDialogOpen(false)
              setDeleteRow(null)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* TASK RE-ASSIGNMENT DIALOG */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm Task Re-assignment</DialogTitle>

        <DialogContent dividers>
          {/* MESSAGE */}
          <Typography sx={{ mb: 2 }}>
            Do you want to assign {selectedTasks.length} task
            {selectedTasks.length !== 1 ? 's' : ''} from{' '}
            <b>{getFullName(formData?.from_user_id)}</b> to{' '}
            <b>{getFullName(formData?.to_user_id)}</b>?
          </Typography>

          {/* TASK LIST */}
          <Box
            sx={{
              maxHeight: 250,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 1,
              p: 1,
            }}
          >
            {previewTasks.map((task) => (
              <Box
                key={task.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
              >
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={selectedTasks.includes(task.id)}
                    onChange={() => toggleTask(task)}
                  />
                  <Typography variant="body2">{task.task_name}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setPreviewModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitSelected}
            disabled={selectedTasks.length === 0}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW LOG DIALOG */}
      <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)} fullWidth maxWidth="md">
        {/* HEADER */}
        <DialogTitle sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            User Replacement Logs
            <Box display="flex" alignItems="center" gap={1}>
              {/* FILTER */}
              <Tooltip title={showLogFilters ? 'Clear Filters' : 'Show Filters'} arrow>
                <IconButton onClick={handleLogFilterToggle}>
                  {showLogFilters ? <FilterAltOffOutlined /> : <FilterAltOutlined />}
                </IconButton>
              </Tooltip>

              {/* CLOSE */}
              <IconButton onClick={() => setLogDialogOpen(false)}>
                <CancelOutlined />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent dividers>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 400,
            }}
          >
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
              {/* HEADER */}
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f7f9ff' }}>
                  <TableCell sx={{ width: '60px' }}>
                    <b>Sl No</b>
                  </TableCell>
                  <TableCell sx={{ width: '60%' }}>
                    <b>Log</b>
                  </TableCell>
                  <TableCell sx={{ width: '180px' }}>
                    <b>Created At</b>
                  </TableCell>
                </TableRow>

                {/* FILTER ROW */}
                {showLogFilters && (
                  <TableRow
                    sx={{
                      backgroundColor: '#fafbff',
                      '& .MuiTableCell-root': {
                        py: 1,
                        px: 1,
                      },
                    }}
                  >
                    <TableCell />

                    {/* SEARCH */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search log..."
                        value={logFilters.search}
                        onChange={(e) =>
                          setLogFilters((prev) => ({
                            ...prev,
                            search: e.target.value.toLowerCase(),
                          }))
                        }
                        sx={filterInputStyle}
                      />
                    </TableCell>

                    {/* DATE RANGE */}
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <TextField
                          type="date"
                          size="small"
                          value={logFilters.fromDate}
                          onChange={(e) =>
                            setLogFilters((prev) => ({
                              ...prev,
                              fromDate: e.target.value,
                            }))
                          }
                          sx={dateInputStyle}
                        />

                        <TextField
                          type="date"
                          size="small"
                          value={logFilters.toDate}
                          onChange={(e) =>
                            setLogFilters((prev) => ({
                              ...prev,
                              toDate: e.target.value,
                            }))
                          }
                          sx={dateInputStyle}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>

              {/* BODY */}
              <TableBody>
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log, index) => (
                    <TableRow key={log.slno || index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{log.log ?? '-'}</TableCell>
                      <TableCell>{formatDate(log.created_at, FORMATS.DATETIME)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No Logs Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UserReplacement
